'use strict'

const flow_ = require('lodash/flow')
const times_ = require('lodash/times')
const flatten_ = require('lodash/flatten')
const isEqual_ = require('lodash/isEqual')
const range_ = require('lodash/range')
const isPlainObject_ = require('lodash/isPlainObject')
const result = require('folktale/result')
const {
  constructUrl,
  parseUrl,
  parseUrlPattern
} = require('dbsm-common/src/dynamic-pages/urlUtils')

const breadcrumbWrapper = require('dbsm-common/src/raven/withBreadcrumbs')
const sequence = require('dbsm-common/src/fp/sequence')
const readWriteModes = require('dbsm-common/src/dataset-configuration/readWriteModes')

const queryResults = require('../helpers/queryResults')

const {
  cleanseRecord,
  createDraft,
  getRecordId,
  hasDraft,
  isRecordPristine
} = require('./records')
const {
  collectMissingRanges,
  freshScope,
  insertRecordAtIndex,
  overwriteRecordAtIndex,
  recordIndexById,
  removeRecordById,
  scopeHasRecord,
  setNewRecordMarkers,
  setSeedInScope,
  storeResultsInScope,
  updateNewRecordMarkers,
  updateNumMatchingRecords
} = require('./scopes')
const {
  doesRecordExist,
  freshStore,
  getDraftOrRecord,
  getScope,
  insertDraft,
  insertRecord,
  iterateScopes,
  readFromRecordsStore,
  removeDraft,
  removeRecord,
  resetDraft,
  setScope,
  storeQueryResults,
  updateRecordFields,
  updateScope
} = require('./store')
const { registrar } = require('./utils')

const WIXDATA_MAX_RECORD_LIMIT = 1000
const ignoreArgs = ['setFieldsValues', 'newRecord']

module.exports = ({
  wixDataProxy,
  collectionName,
  includes,
  readWriteType,
  datasetType,
  dynamicPagesData,
  Raven
}) => {
  let theStore = freshStore()
  const onChangeHandlers = []

  const service = ({ pageSize, sort, filter, seed, controllerId }) => {
    const scopeKey = JSON.stringify({ filter, sort })

    // pageAlign :: Integer -> Integer
    const pageAlignedFromIndex = from => from - from % pageSize

    // pageAlign :: (Integer, Integer) -> Integer
    const pageAlignedToIndex = (from, to) =>
      Math.ceil(
        (pageAlignedFromIndex(from) + (to - pageAlignedFromIndex(from))) /
          pageSize
      ) * pageSize

    // findRecords :: (Integer, Integer) -> Promise QueryResults
    const findRecords = function(offset, length) {
      return wixDataProxy
        .find(collectionName, filter, sort, offset, length, undefined, includes)
        .then(wixDataQueryResults =>
          queryResults.fromWixDataQueryResults(wixDataQueryResults, offset)
        )
    }

    // siblingDynamicPage :: (RELATIVE_DIRECTION) -> Promise
    const siblingDynamicPage = async function({
      relativeDirectionIsBackwards = false
    }) {
      const {
        dynamicUrl,
        userDefinedFilter,
        dynamicUrlPatternFieldsValues
      } = dynamicPagesData
      const { pattern } = parseUrl(dynamicUrl)
      const { fields: patternFields } = parseUrlPattern(pattern)

      function recursiveSiblingDynamicPage(currentFieldIndex) {
        if (currentFieldIndex === undefined)
          currentFieldIndex = patternFields.length - 1
        if (currentFieldIndex === -1) return []

        const indexField = patternFields[currentFieldIndex]

        let query = wixDataProxy.query(collectionName)
        query.filterTree = userDefinedFilter

        if (relativeDirectionIsBackwards) {
          query = query.descending(...patternFields)
        } else {
          query = query.ascending(...patternFields)
        }

        if (relativeDirectionIsBackwards) {
          query = query.lt(
            indexField,
            dynamicUrlPatternFieldsValues[indexField]
          )
        } else {
          query = query.gt(
            indexField,
            dynamicUrlPatternFieldsValues[indexField]
          )
        }

        range_(currentFieldIndex).forEach(
          i =>
            (query = query.eq(
              patternFields[i],
              dynamicUrlPatternFieldsValues[patternFields[i]]
            ))
        )

        return [query.limit(1)].concat(
          recursiveSiblingDynamicPage(currentFieldIndex - 1)
        )
      }

      const joinedQueries = recursiveSiblingDynamicPage().reduce(
        (result, query) => result.or(query)
      )

      const { items } = await joinedQueries.find()
      const firstRecord = items.length > 0 ? items[0] : null
      return firstRecord ? constructUrl(firstRecord, dynamicUrl) : null
    }

    // fetchMissingRanges :: (Scope, Integer, Integer) -> Promise ([QueryResults])
    const fetchMissingRanges = (scope, fromIndex, toIndex) => {
      const fetchFrom = pageAlignedFromIndex(fromIndex)
      const fetchTo = pageAlignedToIndex(fromIndex, toIndex)
      const missingRanges = collectMissingRanges(scope, fetchFrom, fetchTo)
      const correctedRanges = missingRanges
        .filter(([from, length]) => length <= WIXDATA_MAX_RECORD_LIMIT)
        .concat(
          flatten_(
            missingRanges
              .filter(([from, length]) => length > WIXDATA_MAX_RECORD_LIMIT)
              .map(([from, length]) =>
                times_(Math.ceil(length / WIXDATA_MAX_RECORD_LIMIT), x => [
                  from + x * WIXDATA_MAX_RECORD_LIMIT,
                  Math.min(
                    WIXDATA_MAX_RECORD_LIMIT,
                    length - x * WIXDATA_MAX_RECORD_LIMIT
                  )
                ])
              )
          )
        )

      return Promise.all(correctedRanges.map(range => findRecords(...range)))
    }

    // notify :: (Integer, Record, Record, ComponentId) -> Result [*] Error
    const notify = (before, after, componentIdToExclude) =>
      sequence(
        result,
        onChangeHandlers.map(handler =>
          result.try(() =>
            handler(
              cleanseRecord(before),
              cleanseRecord(after),
              componentIdToExclude
            )
          )
        )
      )

    // runApiCommand
    //   :: (([Store -> Store] -> (), Record, Integer, [*]) -> *, () -> *, Integer, [*]) -> *
    const runApiCommand = (f, g, index, ...args) => {
      const recordId = getScope(theStore, scopeKey).records[index]
      const record = getDraftOrRecord(theStore, recordId)

      return record.matchWith({
        Nothing: () => g(),
        Just: ({ value }) => {
          const update = (...flow) => {
            theStore = flow_(...flow)(theStore)
          }

          const notifyIfChanged = componentIdToExclude => {
            const updatedRecord = getDraftOrRecord(
              theStore,
              recordId
            ).getOrElse(null)
            if (!isEqual_(value, updatedRecord)) {
              return notify(value, updatedRecord, componentIdToExclude)
            } else {
              return result.Ok([])
            }
          }

          return f({ update, notifyIfChanged }, value, index, ...args)
        }
      })
    }

    // withRecordByIndex
    //   :: (([Store -> Store] -> (), Record, Integer, [*]) -> *, () -> *) -> (Integer, [*]) -> *
    const withRecordByIndex = (f, g) => (index, ...args) => {
      return runApiCommand(f, g, index, ...args)
    }

    // withRecordByIndexAsync
    //   :: (([Store -> Store] -> (), Record, Integer, [*]) -> *, () -> *) -> (Integer, [*]) -> *
    const withRecordByIndexAsync = (f, g) => async (index, ...args) => {
      return await runApiCommand(f, g, index, ...args)
    }

    // sanitise :: * -> *
    const sanitise = something => {
      const go = object =>
        Object.keys(object)
          .filter(key => key.startsWith('_'))
          .reduce(
            (acc, key) =>
              Object.assign(acc, {
                [key]: isPlainObject_(object[key])
                  ? go(object[key])
                  : object[key]
              }),
            {}
          )

      if (queryResults.Results.hasInstance(something)) {
        return something.map(({ items }) => {
          const sanitisedItems = items.map(item => go(item))

          return { items: sanitisedItems }
        })
      } else if (typeof something === 'object' && something) {
        return go(something)
      } else {
        return something
      }
    }

    // createBreadcrumb :: (String, [*]) -> Breadcrumb
    const createBreadcrumb = (fnName, args = []) => ({
      category: 'recordStore',
      level: 'info',
      message: `${fnName}(${
        ignoreArgs.includes(fnName)
          ? `..${args.length} arguments..`
          : args.map(JSON.stringify)
      }) (${controllerId})`,
      data: {
        scope: scopeKey
      }
    })

    const { withBreadcrumbs, withBreadcrumbsAsync } = breadcrumbWrapper(
      Raven,
      createBreadcrumb,
      sanitise
    )

    const isNewRecord = record =>
      record && !doesRecordExist(theStore, getRecordId(record))

    const api = {
      // getRecords :: (Integer, Integer) -> Promise QueryResults
      getRecords: withBreadcrumbsAsync(
        'getRecords',
        async (fromIndex, length) => {
          const storeReader = readFromRecordsStore(
            scopeKey,
            fromIndex,
            fromIndex + length
          )
          const isWriteOnly = readWriteType === readWriteModes.WRITE
          return storeReader(theStore, isWriteOnly).orElse(async () => {
            const missingRanges = await fetchMissingRanges(
              getScope(theStore, scopeKey),
              fromIndex,
              fromIndex + length
            )
            const notifyUpdatedRecords = (old, current) =>
              Object.keys(old.records)
                .filter(
                  key =>
                    isPlainObject_(current.records[key]) &&
                    current.records[key]._updatedDate >
                      old.records[key]._updatedDate
                )
                .forEach(key => notify(old.records[key], current.records[key]))
            const go = flow_(
              ...missingRanges.map(range =>
                flow_(
                  storeQueryResults(range),
                  updateScope(scopeKey, storeResultsInScope(range))
                )
              )
            )

            const oldStore = theStore
            theStore = go(theStore)
            notifyUpdatedRecords(oldStore, theStore)
            return storeReader(theStore, true)
          })
        }
      ),

      // seed :: () -> Promise ()
      seed: withBreadcrumbsAsync('seed', () => {
        if (getScope(theStore, scopeKey).numSeedRecords === 0) {
          return findRecords(0, pageSize).then(queryResult => {
            const go = flow_(
              storeQueryResults(queryResult),
              updateScope(scopeKey, setSeedInScope(queryResult))
            )

            theStore = go(theStore)
          })
        } else {
          return Promise.resolve()
        }
      }),

      // getSeedRecords :: () -> QueryResults
      getSeedRecords: withBreadcrumbs('getSeedRecords', () =>
        readFromRecordsStore(
          scopeKey,
          0,
          getScope(theStore, scopeKey).numSeedRecords,
          theStore,
          true
        )
      ),
      // getNextDynamicPage :: () -> Promise ()
      getNextDynamicPage: withBreadcrumbs('getNextDynamicPage', () =>
        siblingDynamicPage({ relativeDirectionIsBackwards: false })
      ),

      // getPreviousDynamicPage :: () -> Promise ()
      getPreviousDynamicPage: withBreadcrumbs('getNextDynamicPage', () =>
        siblingDynamicPage({ relativeDirectionIsBackwards: true })
      ),

      // getTotalCount :: () -> Integer
      getMatchingRecordCount: withBreadcrumbs(
        'getMatchingRecordCount',
        () => getScope(theStore, scopeKey).numMatchingRecords
      ),

      // removeRecord :: Integer -> Promise (Result)
      // recordId is a maybe because new records don't have IDs
      removeRecord: withBreadcrumbsAsync(
        'removeRecord',
        withRecordByIndexAsync(
          async ({ update, notifyIfChanged }, record, index) => {
            const recordId = getRecordId(record)
            if (!isNewRecord(record) && recordId) {
              await wixDataProxy.remove(collectionName, recordId)
            }
            const doUpdateMarkers = recordIndex => markers =>
              markers.filter(marker => marker !== recordIndex)
            const updateScopesFlow = iterateScopes(
              (scope, scopeKey) =>
                updateScope(
                  scopeKey,
                  flow_(
                    removeRecordById(recordId),
                    updateNumMatchingRecords(x => x - 1),
                    updateNewRecordMarkers(
                      doUpdateMarkers(recordIndexById(recordId, scope))
                    )
                  )
                ),
              scopeHasRecord(recordId)
            )(theStore)
            update(
              flow_(removeDraft(record), removeRecord(recordId)),
              ...updateScopesFlow
            )
            return notifyIfChanged()
          },
          () => {
            return Promise.resolve(
              result.Error('cannot remove record: index not found')
            )
          }
        )
      ),

      // reset :: () -> ()
      reset: withBreadcrumbs(
        'reset',
        () => (theStore = setScope(scopeKey, freshScope())(theStore))
      ),

      // newRecord :: (Integer, DefaultDraft) -> ()
      newRecord: withBreadcrumbs('newRecord', (index, defaultDraft) => {
        // There Can Only Be One new record
        const draft = createDraft(Object.create(null), defaultDraft)
        const go = flow_(
          insertDraft(draft),
          updateScope(
            scopeKey,
            flow_(
              updateNumMatchingRecords(x => x + 1),
              setNewRecordMarkers([index]),
              insertRecordAtIndex(index, draft)
            )
          )
        )

        theStore = go(theStore)
        notify(null, draft)
        return cleanseRecord(draft)
      }),

      // save :: Integer -> Promise(Record)
      saveRecord: withBreadcrumbsAsync(
        'saveRecord',
        withRecordByIndexAsync(
          async ({ update, notifyIfChanged }, record, index) => {
            const postSaveRecord = await wixDataProxy.save(
              collectionName,
              cleanseRecord(record)
            )
            const doUpdateMarkers = markers =>
              markers.filter(marker => marker !== index)
            update(
              insertRecord(postSaveRecord),
              removeDraft(record),
              updateScope(
                scopeKey,
                flow_(
                  overwriteRecordAtIndex(index, postSaveRecord),
                  updateNewRecordMarkers(doUpdateMarkers)
                )
              )
            )
            notifyIfChanged()

            return cleanseRecord(postSaveRecord)
          },
          () => {
            return Promise.reject(
              new Error('cannot save record: index not found')
            )
          }
        )
      ),

      // setFieldsValues :: (Integer, FieldsValues) -> Result
      setFieldsValues: withBreadcrumbs(
        'setFieldsValues',
        withRecordByIndex(
          (
            { update, notifyIfChanged },
            record,
            _,
            fieldValues,
            componentIdToExclude
          ) => {
            update(updateRecordFields(getRecordId(record), fieldValues))
            return notifyIfChanged(componentIdToExclude)
          },
          () => result.Error('cannot update field values: index not found')
        )
      ),

      // isPristine :: Integer -> Boolean
      isPristine: withBreadcrumbs(
        'isPristine',
        withRecordByIndex((_, record) => isRecordPristine(record), () => true)
      ),

      // hasDraft :: Integer -> Boolean
      hasDraft: withBreadcrumbs(
        'hasDraft',
        withRecordByIndex((_, record) => hasDraft(record), () => false)
      ),

      // isNewRecord :: Integer -> Boolean
      isNewRecord: withBreadcrumbs(
        'isNewRecord',
        withRecordByIndex((_, record) => isNewRecord(record), () => true)
      ),

      // resetDraft :: (Integer, DefaultDraft) -> Result
      resetDraft: withBreadcrumbs(
        'resetDraft',
        withRecordByIndex(
          ({ update, notifyIfChanged }, record, index, defaultDraft) => {
            update(
              isNewRecord(record)
                ? resetDraft(record, defaultDraft)
                : removeDraft(record)
            )
            return notifyIfChanged()
          },
          () => result.Error('cannot reset draft: index not found')
        )
      )
    }

    if (!theStore.scopes[scopeKey]) {
      theStore = flow_(
        storeQueryResults(seed),
        setScope(scopeKey, freshScope()),
        updateScope(scopeKey, setSeedInScope(seed))
      )(theStore)
    }

    return api
  }

  service.onChange = registrar(onChangeHandlers)

  return service
}



// WEBPACK FOOTER //
// ./recordStore/service.js